import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { Form, InputNumber } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { t } from 'i18next';
import { AssetAssignmentSection, CompressorSelectDrawer } from './AssetsAssignmentSection.styles';
import { useStores } from '@/hooks/use-stores';
import { EPButton } from '@/components';
import { breakpoints } from '@/utils/util-breakpoints';
import { ModelSpec } from '@/store/SiteSurveyForm/constants';
import { ImageTags } from '@/constants/CompressorImageTagData.const';
import { ModelSpecSelect } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/CompressorModelSpecSelectComponents/CompressorModelSpecSelectComponents';
import {
  closeCompressorDrawer,
  confirmCompressorSelection,
  getAvailableCompressorsForRoom,
  getModelSpecOptions,
  getRoomIndex,
  handleAddDryer,
  handleAddManualCompressor,
  handleAddRoom,
  handleAddTank,
  handleCompressorSelection,
  handleRemoveCompressor,
  handleRemoveDryer,
  handleRemoveRoom,
  handleRemoveTank,
  handleUpdateCompressorSpecDetails,
  isCompressorNumberDuplicate,
  openCompressorDrawer,
  toggleCompressorSelection,
  updateRoomCount,
  handleAddImagesToCompressor,
  handleEditCompressorImageTag,
  handleRemoveCompressorImage,
  handleUpdateManualCompressorField,
} from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/AssetsAssignmentSection/AssetAssignmentSection.utils';
import { ImageUpload } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload';
import { MAX_IMAGE_UPLOAD_COUNT } from '../ImageUpload/ImageUpload.consts';
import RoomContainerComponent from '../RoomContainer/RoomContainer';
import {
  AssetsAssignmentSectionProps,
  Room,
  hasDuplicateCompressorNumbers,
  hasEmptyCompressorRooms,
  hasMissingCompressorNumbers,
} from '../../RoomConfigurationContent.utils';
import { CompressorNumberInput } from '../CompressorInput/CompressorInputComponents';

const AssetsAssignmentSection: React.FC<
  AssetsAssignmentSectionProps & {
    onDrawerVisibilityChange?: (isVisible: boolean) => void;
    rooms: Room[];
    setRooms: Dispatch<SetStateAction<Room[]>>;
  }
> = observer(
  ({
    form,
    hasMultipleTargetPressures = false,
    onValidationChange,
    onDrawerVisibilityChange,
    onChange,
    rooms,
    setRooms,
  }) => {
    const { siteSurveyStore, imageUploadStore } = useStores();
    const isMobile = window.innerWidth < breakpoints.md;
    const isTablet = window.innerWidth >= breakpoints.md && window.innerWidth < breakpoints.lg;
    const showDrawer = isMobile || isTablet;

    const [drawerVisible, setDrawerVisible] = React.useState(false);
    const [activeRoomId, setActiveRoomId] = React.useState<number | null>(null);
    const [isImageUploadModelOpen, setIsImageUploadModelOpen] = React.useState(false);
    const roomCount = Form.useWatch('compressorRooms', form);
    const otherRoomCount = Form.useWatch('otherCompressorRooms', form);
    const pressureType = form.getFieldValue('pressureType');
    const activeRoom = activeRoomId !== null ? rooms.find(r => r.id === activeRoomId) : null;
    const hasDuplicateNumbers = hasDuplicateCompressorNumbers(rooms);
    const hasEmptyRooms = hasEmptyCompressorRooms(rooms);
    const hasMissingNumbers = hasMissingCompressorNumbers(rooms);

    if (onValidationChange) {
      onValidationChange(!(hasDuplicateNumbers || hasEmptyRooms || hasMissingNumbers));
    }
    if (!siteSurveyStore.tankForms) {
      siteSurveyStore.tankForms = {};
    }

    useEffect(() => {
      const formData = form.getFieldValue(pressureType);
      if (formData?.rooms) {
        setRooms(formData.rooms);
      } else {
        setRooms([]);
        form.setFieldValue(pressureType, { rooms: [] });
      }
    }, [form, pressureType]);

    useEffect(() => {
      let targetCount = 0;

      if (roomCount === undefined) {
        targetCount = rooms.length;
      } else if (roomCount === 'other') {
        targetCount = otherRoomCount || 1;
      } else {
        targetCount = roomCount;
      }

      if (rooms.length !== targetCount) {
        updateRoomCount(targetCount, rooms, setRooms, form, pressureType, onChange);
      }
    }, [roomCount, otherRoomCount, rooms.length]);

    const renderCompressorNumberInput = (roomId: number, compressorId: string, index: number) => {
      const roomIndex = getRoomIndex(rooms, roomId);
      const currentRoom = rooms[roomIndex];
      const compressorData = currentRoom?.compressorData?.[index];

      return (
        <CompressorNumberInput
          key={`${roomIndex}-${index}`}
          index={index}
          pressureType={pressureType}
          roomIndex={roomIndex}
          form={form}
          isCompressorNumberDuplicate={isCompressorNumberDuplicate}
          initialValue={compressorData?.number}
          compressorId={compressorId}
          roomId={roomId}
        />
      );
    };

    const renderModelSpecSelect = (
      roomId: number,
      compressorId: string,
      index: number,
      onNoMatchingSpecSelect?: () => void,
    ) => {
      const roomIndex = getRoomIndex(rooms, roomId);
      const currentRoom = rooms[roomIndex];
      const compressorData = currentRoom?.compressorData?.[index];

      return (
        <ModelSpecSelect
          roomId={roomId}
          compressorId={compressorId}
          index={index}
          pressureType={pressureType}
          roomIndex={roomIndex}
          modelSpecs={getModelSpecOptions(siteSurveyStore) as unknown as ModelSpec[]}
          form={form}
          initialValue={compressorData?.modelSpec}
          onNoMatchingSpecSelect={onNoMatchingSpecSelect}
        />
      );
    };

    const openImageUpload = (compressorId: string, currentImageCount: number) => {
      imageUploadStore.setDeviceId(compressorId);
      imageUploadStore.setMaxFiles(MAX_IMAGE_UPLOAD_COUNT - currentImageCount);

      setIsImageUploadModelOpen(true);
    };

    const closeImageUpload = () => {
      imageUploadStore.revokeImages();
      imageUploadStore.reset();
      setIsImageUploadModelOpen(false);
    };

    const addManualCompressor = (roomId: number) =>
      handleAddManualCompressor(roomId, form, rooms, pressureType, setRooms, onChange);

    const removeCompressor = (roomId: number, compressorIndex: number) =>
      handleRemoveCompressor(roomId, compressorIndex, pressureType, form, setRooms, onChange);

    const updateCompressorManualField = (roomId: number, compressorIndex: number, field: string, value: string) =>
      handleUpdateManualCompressorField(roomId, compressorIndex, field, value, pressureType, form, setRooms, onChange);

    const removeCompressorImage = (roomId: number, compressorIndex: number, tag: ImageTags, imageId: number) =>
      handleRemoveCompressorImage(roomId, compressorIndex, imageId, tag, rooms, pressureType, form, setRooms, onChange);

    const editCompressorImageTag = (
      roomId: number,
      compressorIndex: number,
      id: number,
      newTag: ImageTags,
      currentTag: ImageTags,
    ) =>
      handleEditCompressorImageTag(
        roomId,
        compressorIndex,
        id,
        newTag,
        currentTag,
        rooms,
        pressureType,
        form,
        setRooms,
        onChange,
      );

    const updateCompressorSpecSheet = (roomId: number, compressorId: string, field: string, value: number | null) =>
      handleUpdateCompressorSpecDetails(
        roomId,
        compressorId,
        field,
        value,
        rooms,
        pressureType,
        form,
        setRooms,
        onChange,
      );

    const addTank = (roomId: number) => handleAddTank(roomId, pressureType, form, setRooms, onChange);

    const removeTank = (roomId: number, tankId: string) =>
      handleRemoveTank(roomId, tankId, pressureType, form, setRooms);

    const selectCompressor = (roomId: number, selectedSerialNumbers: string[]) =>
      handleCompressorSelection(
        roomId,
        selectedSerialNumbers,
        rooms,
        form,
        pressureType,
        siteSurveyStore,
        setRooms,
        onChange,
      );

    const addImagesToCompressor = () =>
      handleAddImagesToCompressor(
        rooms,
        imageUploadStore,
        pressureType,
        form,
        setRooms,
        onChange,
        setIsImageUploadModelOpen,
      );

    return (
      <AssetAssignmentSection>
        <Form.Item name="compressorRooms" initialValue={siteSurveyStore.siteConfigurationData.compressorRooms} hidden>
          <InputNumber />
        </Form.Item>

        <div className="section-header">
          <div className="section-title">
            {hasMultipleTargetPressures
              ? `${t('Assets assignment')} - ${pressureType.charAt(0).toUpperCase() + pressureType.slice(1)} ${t(
                  'pressure',
                )}`
              : t('Assets assignment')}
          </div>
        </div>
        {rooms.map(room => (
          <RoomContainerComponent
            key={room.id}
            room={room}
            pressureType={pressureType}
            showDrawer={showDrawer}
            isMobile={isMobile}
            isTablet={isTablet}
            onRemoveRoom={roomId => handleRemoveRoom(roomId, pressureType, form, setRooms, onChange)}
            onAddManualCompressor={addManualCompressor}
            onRemoveCompressor={removeCompressor}
            onUpdateManualCompressorField={updateCompressorManualField}
            onOpenCompressorImageUpload={openImageUpload}
            onRemoveCompressorImage={removeCompressorImage}
            onEditCompressorImageTag={editCompressorImageTag}
            onUpdateCompressorSpecDetails={updateCompressorSpecSheet}
            onAddTank={() => addTank(room.id)}
            onRemoveTank={removeTank}
            onAddDryer={() => handleAddDryer(room.id, pressureType, form, setRooms, onChange)}
            onRemoveDryer={(roomId, dryerId) => handleRemoveDryer(roomId, dryerId, pressureType, form, setRooms)}
            getAvailableCompressorsForRoom={roomId => getAvailableCompressorsForRoom(roomId, rooms, siteSurveyStore)}
            getRoomIndex={getRoomIndex}
            renderCompressorNumberInput={renderCompressorNumberInput}
            renderModelSpecSelect={renderModelSpecSelect}
            handleCompressorSelection={selectCompressor}
            openCompressorDrawer={() =>
              openCompressorDrawer(room.id, setActiveRoomId, setDrawerVisible, onDrawerVisibilityChange)
            }
            form={form}
            rooms={rooms}
          />
        ))}

        {rooms.length < 15 && (
          <EPButton onClick={() => handleAddRoom(form, pressureType, setRooms, onChange)} className="add-room-button">
            {t('Add room')}
          </EPButton>
        )}

        <CompressorSelectDrawer
          title={t('Select compressors')}
          placement="bottom"
          open={drawerVisible}
          onClose={() => closeCompressorDrawer(setDrawerVisible, onDrawerVisibilityChange)}
          height="50%"
          footer={
            <div className="drawer-footer">
              <EPButton
                type="primary"
                onClick={() => confirmCompressorSelection(setDrawerVisible, onDrawerVisibilityChange)}
              >
                {t('Continue')}
              </EPButton>
            </div>
          }
          className="compressor-select-drawer"
        >
          <div className="drawer-content">
            <div className="compressor-list">
              {activeRoomId !== null &&
                getAvailableCompressorsForRoom(activeRoomId, rooms, siteSurveyStore).map(option => {
                  const isSelected = activeRoom?.compressorsInRoom.includes(option.value as string);
                  return (
                    <div
                      key={option.value}
                      className={`compressor-item ${isSelected ? 'selected' : ''}`}
                      onClick={() =>
                        toggleCompressorSelection(
                          option.value as string,
                          activeRoomId,
                          activeRoom,
                          siteSurveyStore,
                          rooms,
                          form,
                          pressureType,
                          setRooms,
                          onChange,
                        )
                      }
                    >
                      <span className="compressor-item-text">{option.label}</span>
                      {isSelected && <CheckOutlined className="selected-icon" />}
                    </div>
                  );
                })}
            </div>
          </div>
        </CompressorSelectDrawer>

        <ImageUpload open={isImageUploadModelOpen} onClose={closeImageUpload} onComplete={addImagesToCompressor} />
      </AssetAssignmentSection>
    );
  },
);

export default AssetsAssignmentSection;
